import { getRuleTemplates } from "@/services/schemaBuilderApi";
import { SCHEMA_RULE_CATEGORIES } from "@/types/schemaBuilder";
import type { SchemaDataType, SchemaRuleCategory, SchemaRulePreset } from "@/types/schemaBuilder";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface AddRuleModalProps {
  column?: {
    fieldId: string;
    id: string;
    name: string;
    dataType: SchemaDataType;
    example: string;
  };
  existingRuleTypes?: Set<string>;
  initialCategory?: SchemaRuleCategory;
  scopeFilter?: "column" | "schema";
  onClose: () => void;
  onSelectPreset: (preset: SchemaRulePreset, category: SchemaRuleCategory) => void;
}

const CATEGORIES = SCHEMA_RULE_CATEGORIES;

export function AddRuleModal({
  column,
  existingRuleTypes,
  initialCategory,
  scopeFilter,
  onClose,
  onSelectPreset,
}: AddRuleModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<SchemaRuleCategory>(
    initialCategory ?? "All"
  );
  const [allPresets, setAllPresets] = useState<SchemaRulePreset[]>([]);

  useEffect(() => {
    void getRuleTemplates()
      .then(setAllPresets)
      .catch(() => {});
  }, []);

  const applicablePresets = useMemo(() => {
    return allPresets.filter((preset) => {
      // valid_number/valid_date/valid_boolean are created automatically, never manually.
      if (
        preset.backendRuleType === "valid_number" ||
        preset.backendRuleType === "valid_date" ||
        preset.backendRuleType === "valid_boolean"
      ) {
        return false;
      }
      if (scopeFilter && (preset.scope ?? "column") !== scopeFilter) return false;
      if (column && !preset.applicableDataTypes.includes(column.dataType)) return false;
      // Hide rules already on this column with no configurable params (rules with
      // params can be re-added with different values)
      if (existingRuleTypes?.has(preset.backendRuleType)) {
        const properties =
          (preset.parametersSchema?.properties as
            | Record<string, Record<string, unknown>>
            | undefined) ?? {};
        const explicitRequired = (preset.parametersSchema?.required as string[] | undefined) ?? [];
        const requiredUserInput =
          explicitRequired.length > 0
            ? explicitRequired.filter((k) => properties[k]?.type !== "boolean")
            : Object.entries(properties)
                .filter(([, p]) => p.type !== "boolean")
                .map(([k]) => k);
        if (requiredUserInput.length === 0) return false;
      }
      return true;
    });
  }, [allPresets, column, existingRuleTypes, scopeFilter]);

  useEffect(() => {
    if (
      selectedCategory !== "All" &&
      applicablePresets.length > 0 &&
      !applicablePresets.some((p) => p.category === selectedCategory)
    ) {
      setSelectedCategory("All");
    }
  }, [applicablePresets, selectedCategory]);

  const visiblePresets = useMemo(() => {
    if (selectedCategory === "All") return applicablePresets;

    return applicablePresets.filter((preset) => preset.category === selectedCategory);
  }, [applicablePresets, selectedCategory]);

  function hasRulesForCategory(category: SchemaRuleCategory) {
    if (category === "All") return applicablePresets.length > 0;

    return applicablePresets.some((preset) => preset.category === category);
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="w-[760px] rounded-lg bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between border-b border-dotted border-blue-300 pb-3">
          <div>
            <div className="flex items-center gap-8">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Add Rule</h2>

              {column ? (
                <>
                  <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {column.name}
                  </span>

                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {column.dataType}
                  </span>

                  <span className="text-sm text-[var(--color-text-tertiary)]">
                    Ex. {column.example || "---"}
                  </span>
                </>
              ) : (
                <span className="text-sm text-[var(--color-text-secondary)]">
                  Cross Column Rules
                </span>
              )}
            </div>

            <p className="mt-3 text-sm text-[var(--color-text-primary)]">
              {column
                ? "Showing all applicable rules for column data type"
                : "Showing all cross-column rule types"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid h-[360px] grid-cols-[120px_1fr] gap-5">
          <div>
            <h3 className="mb-2 text-xs font-semibold text-[var(--color-text-primary)]">
              Categories
            </h3>

            <div className="space-y-2">
              {CATEGORIES.map((category) => {
                const hasRules = hasRulesForCategory(category);
                const isSelected = selectedCategory === category;

                return (
                  <button
                    key={category}
                    type="button"
                    disabled={!hasRules}
                    onClick={() => setSelectedCategory(category)}
                    className={`h-8 w-full rounded border px-3 text-xs transition ${
                      isSelected
                        ? "border-[var(--color-action-primary)] bg-green-50 text-[var(--color-action-primary)]"
                        : "border-[var(--color-border)] bg-white text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
                    } ${
                      !hasRules
                        ? "cursor-not-allowed border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] text-[var(--color-text-tertiary)] opacity-60"
                        : ""
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="overflow-y-auto rounded border border-dotted border-blue-300 p-3 pr-2">
            {visiblePresets.length > 0 ? (
              <div className="space-y-3">
                {visiblePresets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => onSelectPreset(preset, selectedCategory)}
                    className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-4 text-left transition hover:border-[var(--color-action-primary)] hover:bg-white"
                  >
                    <div className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {preset.title}
                    </div>

                    <div className="mt-2 text-xs leading-5 text-[var(--color-text-secondary)]">
                      {preset.description}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded border border-dashed border-[var(--color-border)] p-4 text-xs text-[var(--color-text-tertiary)]">
                No rules available for this data type.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
